import { useEffect, useRef, useState } from 'react';

import type { UseVideoPreviewOptions, UseVideoPreviewReturn } from './useVideoPreview.types';

export const useVideoPreview = ({ videoCover }: UseVideoPreviewOptions): UseVideoPreviewReturn => {
  const [isVideoReady, setIsVideoReady] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const videoPreloadRef = useRef<HTMLVideoElement | null>(null);

  // Show video when it exists, is ready to play, and user is hovering
  const showVideo = isVideoReady && isHovered && Boolean(videoCover);

  // Preload video and wait for it to be ready
  useEffect(() => {
    if (videoCover && videoPreloadRef.current) {
      const video = videoPreloadRef.current;
      video.load();

      // Wait for enough data to be loaded to show first frame
      const handleLoadedData = () => {
        // Ensure we're at the first frame
        if (video.readyState >= 2) {
          video.currentTime = 0;
          setIsVideoReady(true);
        }
      };

      const handleCanPlay = () => {
        // Video can start playing
        setIsVideoReady(true);
      };

      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('canplay', handleCanPlay);

      // Check if already loaded
      if (video.readyState >= 2) {
        video.currentTime = 0;
        setIsVideoReady(true);
      }

      return () => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('canplay', handleCanPlay);
      };
    }
  }, [videoCover]);

  const handleMouseEnter = () => {
    setIsHovered(true);
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
  };

  return {
    isVideoReady,
    isHovered,
    videoPreloadRef,
    showVideo,
    handleMouseEnter,
    handleMouseLeave,
  };
};
